import { createRouter, createWebHistory } from "vue-router";
import AuthService from "../services/auth.js";
import unsavedChangesStore from "../stores/unsavedChangesStore.js";
import Home from "../views/Home.vue";
import Login from "../views/Login.vue";
import Invalid from "../views/Invalid.vue";

// --- Dynamic router logic based on domain ---
const hostname = typeof window !== "undefined" ? window.location.hostname : "";

let routes = [];
if (hostname === "offerte.netnest.be") {
  // Alleen korte /:id route voor publieke offertes
  routes = [
    {
      path: "/companies",
      name: "Companies",
      component: () => import("../views/Companies.vue"),
      meta: { requiresAuth: true, allowedRoles: ["administrator", "manager"] },
    },
    {
      path: "/:id",
      name: "PublicOfferte",
      component: () => import("../views/PublicOfferte.vue"),
    },
  ];
} else {
  // All platform routes for pms.netnest.be and others
  routes = [
    {
      path: "/users/:id",
      name: "UserDetail",
      component: () => import("../views/UserDetail.vue"),
      meta: { requiresAuth: true },
      props: true,
    },
    {
      path: "/",
      name: "Home",
      component: Home,
      meta: { requiresAuth: true },
    },
    {
      path: "/documentation",
      name: "Documentation",
      component: () => import("../views/Documentation.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/documentation/:tool",
      name: "DocumentationTool",
      component: () => import("../views/DocumentationTool.vue"),
      meta: { requiresAuth: true },
      props: true,
    },
    {
      path: "/offertes",
      name: "Offertes",
      component: () => import("../views/Offertes.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/offertes/nieuw",
      name: "OfferteOpstellen",
      component: () => import("../views/OfferteOpstellen.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/contacts",
      name: "Contacts",
      component: () => import("../views/Contacts.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/contacts/:id",
      name: "ContactDetail",
      component: () => import("../views/ContactDetail.vue"),
      meta: { requiresAuth: true },
      props: true,
    },
    {
      path: "/contacts/:id/edit",
      name: "ContactEdit",
      component: () => import("../views/ContactEdit.vue"),
      meta: { requiresAuth: true },
      props: true,
    },
    {
      path: "/deals",
      name: "Deals",
      component: () => import("../views/Deals.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/deals/nieuw",
      name: "DealAdd",
      component: () => import("../views/DealAdd.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/deals/:id",
      name: "DealDetail",
      component: () => import("../views/DealDetail.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/deals/forecast",
      name: "Forecast",
      component: () => import("@/views/Forecast.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/meetings/:id",
      name: "MeetingDetail",
      component: () => import("../views/MeetingDetail.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/tasks/:id",
      name: "TaskDetail",
      component: () => import("../views/TaskDetail.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/calls/:id",
      name: "CallDetail",
      component: () => import("../views/CallDetail.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/planning",
      name: "Planning",
      component: () => import("../views/Planning.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/whereAbouts",
      name: "WhereAbouts",
      component: () => import("../views/WhereAbouts.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/projects/new",
      name: "AddProject",
      component: () => import("../views/AddProject.vue"),
      meta: { requiresAuth: true, allowedRoles: ["administrator", "manager"] },
    },
    {
      path: "/influencers",
      name: "Influencers",
      component: () => import("../views/Influencers.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/influencers/add",
      name: "AddInfluencer",
      component: () => import("../views/AddInfluencer.vue"),
      meta: {
        requiresAuth: true,
        allowedRoles: ["administrator", "manager", "intern"],
      },
    },
    {
      path: "/articles",
      name: "Artikelen",
      component: () => import("../views/ArticlesPage.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/articles/nieuw",
      name: "ArticleAdd",
      component: () => import("../views/ArticleAdd.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/articles/:id",
      name: "ArticleDetail",
      component: () => import("../views/ArticleDetail.vue"),
      meta: { requiresAuth: true },
      props: true,
    },
    {
      path: "/influencers/edit/:id",
      name: "EditInfluencer",
      component: () => import("../views/EditInfluencer.vue"),
      meta: {
        requiresAuth: true,
        allowedRoles: ["administrator", "manager", "intern"],
      },
    },
    {
      path: "/artists",
      name: "Artists",
      component: () => import("../views/Artists.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/artists/:id",
      name: "ArtistDetail",
      component: () => import("../views/ArtistDetail.vue"),
      meta: { requiresAuth: true },
      props: true,
    },
    {
      path: "/login",
      name: "Login",
      component: Login,
      meta: { guestOnly: true },
    },
    {
      path: "/prospectie",
      name: "Prospectie",
      component: () => import("../views/Prospectie.vue"),
      meta: { requiresAuth: true, allowedRoles: ["administrator", "manager"] },
    },
    {
      path: "/email-templates",
      name: "EmailTemplates",
      component: () => import("../views/EmailTemplates.vue"),
      meta: { requiresAuth: true, allowedRoles: ["administrator", "manager"] },
    },
    {
      path: "/timetracker",
      name: "Timetracker",
      component: () => import("../views/Timetracker.vue"),
      meta: { requiresAuth: true, allowedRoles: ["administrator", "manager"] },
    },
    {
      path: "/tools",
      name: "Tools",
      component: () => import("../views/Tools.vue"),
      meta: { requiresAuth: true, allowedRoles: ["administrator", "manager"] },
    },
    {
      path: "/chat",
      name: "Chat",
      component: () => import("../views/Chat.vue"),
      meta: {
        requiresAuth: true,
        allowedRoles: ["administrator", "manager", "employee", "intern"],
      },
    },
    {
      path: "/tools/file-converter",
      name: "FileConverter",
      component: () => import("../views/FileConverter.vue"),
      meta: { requiresAuth: true, allowedRoles: ["administrator", "manager"] },
    },
    {
      path: "/tools/compress",
      name: "Compressor",
      component: () => import("../components/Compressor.vue"),
      meta: { requiresAuth: true, allowedRoles: ["administrator", "manager"] },
    },
    {
      path: "/agenda",
      name: "Agenda",
      component: () => import("../views/Agenda.vue"),
      meta: { requiresAuth: true, allowedRoles: ["administrator", "manager"] },
    },
    {
      path: "/companies",
      name: "Companies",
      component: () => import("../views/Companies.vue"),
      meta: { requiresAuth: true, allowedRoles: ["administrator", "manager"] },
    },
    {
      path: "/companies/nieuw",
      name: "CompanyAdd",
      component: () => import("../views/CompanyAdd.vue"),
      meta: { requiresAuth: true, allowedRoles: ["administrator", "manager"] },
    },
    {
      path: "/companies/:id",
      name: "CompanyDetail",
      component: () => import("../views/CompanyDetail.vue"),
      meta: { requiresAuth: true, allowedRoles: ["administrator", "manager"] },
      props: true,
    },
    {
      path: "/companies/:id/edit",
      name: "CompanyEdit",
      component: () => import("../views/CompanyEdit.vue"),
      meta: { requiresAuth: true, allowedRoles: ["administrator", "manager"] },
      props: true,
    },
    {
      path: "/projects",
      name: "Projects",
      component: () => import("../views/Projects.vue"),
      meta: { requiresAuth: true, allowedRoles: ["administrator", "manager"] },
    },
    {
      path: "/projects/tasks/all",
      name: "ProjectsTasks",
      component: () => import("../views/ProjectsTasks.vue"),
      meta: { requiresAuth: true, allowedRoles: ["administrator", "manager"] },
    },
    {
      path: "/projects/board/all",
      name: "ProjectsBoard",
      component: () => import("../views/ProjectsBoard.vue"),
      meta: { requiresAuth: true, allowedRoles: ["administrator", "manager"] },
    },
    {
      path: "/projects/timeline/all",
      name: "ProjectsTimeline",
      component: () => import("../views/ProjectsTimeline.vue"),
      meta: { requiresAuth: true, allowedRoles: ["administrator", "manager"] },
    },
    {
      path: "/projects/:id",
      name: "ProjectDetails",
      component: () => import("../views/ProjectDetails.vue"),
      meta: { requiresAuth: true, allowedRoles: ["administrator", "manager"] },
    },
    {
      path: "/projects/:id/edit",
      name: "ProjectEdit",
      component: () => import("../views/AddProject.vue"),
      meta: { requiresAuth: true, allowedRoles: ["administrator", "manager"] },
      props: true,
    },
    {
      path: "/team",
      name: "Team",
      component: () => import("../views/Team.vue"),
      meta: { requiresAuth: true, allowedRoles: ["administrator", "manager"] },
    },
    {
      path: "/company-map",
      name: "CompanyMap",
      component: () => import("../views/CompanyForm.vue"),
      meta: { requiresAuth: true, allowedRoles: ["administrator", "manager"] },
    },
    {
      path: "/company",
      name: "CompanyInfo",
      component: () => import("../views/CompanyInfo.vue"),
      meta: { requiresAuth: true, allowedRoles: ["administrator"] },
    },
    {
      path: "/company-database-map",
      name: "CompanyDatabaseMap",
      component: () => import("../views/CompanyMap.vue"),
      meta: { requiresAuth: true, allowedRoles: ["administrator", "manager"] },
    },
    {
      path: "/users",
      name: "UserManagement",
      component: () => import("../views/UserManagement.vue"),
      meta: { requiresAuth: true, allowedRoles: ["administrator", "manager"] },
    },
    {
      path: "/roles",
      name: "RoleManagement",
      component: () => import("../views/RoleManagement.vue"),
      meta: { requiresAuth: true, allowedRoles: ["administrator"] },
    },
    {
      path: "/admin",
      name: "AdminSettings",
      component: () => import("../views/AdminSettings.vue"),
      meta: { requiresAuth: true, allowedRoles: ["administrator"] },
    },
    {
      path: "/profile",
      name: "Profile",
      component: () => import("../views/Profile.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/settings",
      name: "Settings",
      component: () => import("../views/Settings.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/offertes/:id",
      name: "OfferteDetail",
      component: () => import("../views/OfferteDetail.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/offertes/edit/:id",
      name: "OfferteBewerken",
      component: () => import("../views/OfferteBewerken.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/:pathMatch(.*)*",
      redirect: "/invalid",
    },
    {
      path: "/invalid",
      name: "Invalid",
      component: Invalid,
    },
  ];
}

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { left: 0, top: 0 };
  },
});

// Global navigation guard (only for platform routes)
if (hostname !== "offerte.netnest.be") {
  router.beforeEach(async (to, from, next) => {
    // Check for unsaved changes when leaving Settings
    if (from.name === "Settings" && to.name !== "Settings") {
      const settingsComponent = unsavedChangesStore.currentComponent;
      if (settingsComponent && settingsComponent.hasUnsavedChanges) {
        settingsComponent.showUnsavedChangesModal = true;
        unsavedChangesStore.setPendingNavigation(next);
        return;
      }
    }

    // Initialize auth service if not done yet
    if (!AuthService.user && localStorage.getItem("authToken")) {
      await AuthService.initialize();
    }

    // Guest-only routes (like login)
    if (to.meta && to.meta.guestOnly && AuthService.isAuthenticated) {
      return next({ path: "/" });
    }

    // Require authentication
    if (to.meta && to.meta.requiresAuth) {
      if (!AuthService.isAuthenticated) {
        return next({ path: "/login", query: { redirect: to.fullPath } });
      }

      // Admins mogen altijd overal naartoe
      const user = AuthService.user || {};
      if (user.role === "admin" || user.role === "administrator") {
        return next();
      }

      // Rights-based access control
      const rights = user.rights || {};
      // Map route name to right key (adjust as needed)
      const routeRightsMap = {
        Agenda: "agenda",
        Companies: "companies",
        Contacts: "contacts",
        Projects: "projectsInvoices", // or projectsExpenses, projectsSubscriptions
        Planning: "planning",
        Deals: "dealsOffertes",
        Offertes: "dealsOffertes",
        Timetracker: "timeTracking",
        Tools: "settings",
        Chat: "settings",
        UserManagement: "userManagement",
        AdminSettings: "settings",
        Prospectie: "prospectie",
        Influencers: "crmCompanies",
        EmailTemplates: "articles",
        Documentation: "export",
      };
      const rightKey = routeRightsMap[to.name];
      if (rightKey && !rights[rightKey]) {
        return next({ path: "/" });
      }

      // Fallback: allowedRoles check (legacy)
      if (
        to.meta.allowedRoles &&
        Array.isArray(to.meta.allowedRoles) &&
        to.meta.allowedRoles.length
      ) {
        const allowed = to.meta.allowedRoles.some((r) =>
          AuthService.hasRole(r),
        );
        if (!allowed) {
          return next({ path: "/" });
        }
      }
    }
    return next();
  });
}

export default router;
